// ============================================================
// GameScreen.tsx — Canvas wrapper, modes, gauge UI, power-ups, scaling
// ============================================================

import { useEffect, useRef, useState } from 'react';
import {
  initGame, startGame, stopGame, forceGameOver,
  castWildcardBall, castBarrage, castLaser, isLaserActive,
  castFreeze, castBlackout, castScramble, castFlood,
  getOpponentScore, setOpponentScore,
  getCurrentScore, getCurrentLevel,
  getPowerCosts, setRushOverride,
  setMultiTouchMode, setDuelScoreCallback,
  getDebugSnapshot,
  applyInterference,
  setMiroirMode, setOnSendWordClaim, applyAcceptedClaim,
} from '../game/engine';
import { isLoggedIn, submitScore, submitBsReport, getUser } from '../api/client';
import {
  sendInterference as mpSendInterference,
  sendChat as mpSendChat,
  sendScoreUpdate as mpSendScoreUpdate,
  sendGameOver as mpSendGameOver,
  sendBsRequest as mpSendBsRequest,
  sendBsSnapshot as mpSendBsSnapshot,
  sendWordClaim as mpSendWordClaim,
  setMultiplayerHandler,
  isMultiplayerConnected,
  getMultiplayerState,
  type MultiplayerEvent,
} from '../multiplayer/client';
import { setMultiplayerCallback } from '../game/engine';
import { startBot, type BotInstance } from '../game/bot';
import { getElo, updateEloFromMatch } from '../game/elo';
import { t } from '../i18n';
import type { GameStats } from '../game/types';
import type { GameMode } from '../App';

const REF_W = 480;
const REF_H = 850;

interface Props {
  onGameOver: (stats: GameStats) => void;
  seedOverride?: number;
  mode: GameMode;
  aiName?: string;
  roster?: { id: string; name: string }[];
  gameMode?: 'normal' | 'miroir';
}

export default function GameScreen({ onGameOver, seedOverride, mode, aiName, roster, gameMode = 'normal' }: Props) {
  const started = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [gauge, setGauge] = useState(0);
  const [gaugeMax, setGaugeMax] = useState(100);
  const [oppScore, setOppScore] = useState(0);
  const [peerScores, setPeerScores] = useState<Record<string, { name: string; score: number; finished: boolean }>>(() => {
    const init: Record<string, { name: string; score: number; finished: boolean }> = {};
    for (const p of roster || []) init[p.id] = { name: p.name, score: 0, finished: false };
    return init;
  });
  const [duel, setDuel] = useState({ p1: 0, p2: 0 });
  const [laserArmedUI, setLaserArmedUI] = useState(false);
  const [chatMessages, setChatMessages] = useState<Array<{ from: 'me' | 'them'; text: string; ts: number }>>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const [unreadChat, setUnreadChat] = useState(0);
  const [bsToast, setBsToast] = useState<string | null>(null);
  const chatListRef = useRef<HTMLDivElement>(null);
  // Buffer for in-flight BS reports waiting on peer snapshots.
  // Keyed by requestId; each entry collects responses until the timer fires.
  const bsBuffersRef = useRef<Map<string, { fromName: string; fromId: string; snapshot: unknown }[]>>(new Map());
  // Online match outcome: set BEFORE forceGameOver so the engine's onGameOver
  // can attach win/loss + loser name to the GameStats it ships out.
  const matchResultRef = useRef<{ result: 'win' | 'loss'; loserName?: string } | null>(null);
  // Live builder for the local debug snapshot. Held in a ref so the
  // multiplayer event handler (registered once) can always read the latest
  // closure without re-registering on every state change.
  const buildLocalSnapshotRef = useRef<() => Record<string, unknown>>(() => ({}));
  const costs = getPowerCosts();

  const isOnline = mode === 'online';
  const isAI = mode === 'ai';
  const isDuel = mode === 'localDuel';
  const showOpponent = isOnline || isAI;

  // Bot opponent for AI mode — strength matches the player's hidden Elo.
  const botRef = useRef<BotInstance | null>(null);
  const botEloRef = useRef<number>(0);
  useEffect(() => {
    if (!isAI) return;
    const playerElo = getElo();
    botEloRef.current = playerElo;
    const bot = startBot(playerElo, (points) => {
      const next = getOpponentScore() + points;
      setOpponentScore(next);
      setOppScore(next);
    });
    botRef.current = bot;
    return () => { bot.stop(); botRef.current = null; };
  }, [isAI]);

  // Online: subscribe to multiplayer events for peer scores, interference, chat, gameOver.
  useEffect(() => {
    if (!isOnline) return;
    setMultiplayerHandler((event: MultiplayerEvent) => {
      if (event.type === 'peerScore') {
        setPeerScores(prev => {
          const next = { ...prev };
          const existing = next[event.playerId] || { name: event.playerId.slice(0, 4), score: 0, finished: false };
          next[event.playerId] = { ...existing, score: event.score };
          return next;
        });
        // Keep the legacy single-opponent value in sync (top peer score) for the
        // existing engine.opponentScore plumbing used by interference logic.
        const top = Math.max(event.score, getOpponentScore());
        setOpponentScore(top);
        setOppScore(top);
      } else if (event.type === 'chat') {
        setChatMessages(prev => [...prev.slice(-29), { from: 'them', text: `${event.from}: ${event.text}`, ts: event.ts }]);
        setUnreadChat(u => (chatOpen ? 0 : u + 1));
      } else if (event.type === 'interference') {
        // Apply the spell to OUR playfield. Without this, peers were sending
        // interference but it never landed (engine.applyInterference was never wired).
        applyInterference(event.effect);
      } else if (event.type === 'peerGameOver') {
        // First game-over ends the match for everyone. Record that we won
        // (a peer busted, not us) so GameOverScreen can show "YOU WIN".
        if (!matchResultRef.current) {
          const peer = peerScores[event.playerId];
          matchResultRef.current = {
            result: 'win',
            loserName: peer?.name || event.playerId.slice(0, 4),
          };
        }
        setPeerScores(prev => {
          const next = { ...prev };
          const existing = next[event.playerId];
          if (existing) next[event.playerId] = { ...existing, score: event.finalScore, finished: true };
          return next;
        });
        forceGameOver();
      } else if (event.type === 'bsRequest') {
        // A peer pressed the BS button — capture our screen state and reply.
        // Use the ref directly so the handler (registered once per effect run)
        // always reads the latest builder without re-registering.
        const snap = buildLocalSnapshotRef.current();
        mpSendBsSnapshot(event.requestId, event.fromId, snap);
      } else if (event.type === 'bsSnapshot') {
        // We pressed BS and a peer replied — buffer it for the bundler.
        const buf = bsBuffersRef.current.get(event.requestId);
        if (buf) {
          buf.push({ fromName: event.from, fromId: event.fromId, snapshot: event.snapshot });
        }
      } else if (event.type === 'wordAccepted') {
        // Miroir: a claim landed — pop the matching balls everywhere. Score
        // is added only on the claimant's client (applyAcceptedClaim handles
        // the credit gating).
        const myId = getMultiplayerState().playerId;
        const isSelf = event.claimantId === myId;
        applyAcceptedClaim(event.ballIds, isSelf, event.points);
        // Update the peer score map for non-self claimants so the sidebar
        // reflects the win immediately (don't wait for the heartbeat).
        if (!isSelf) {
          setPeerScores(prev => {
            const next = { ...prev };
            const existing = next[event.claimantId] || { name: event.claimantName, score: 0, finished: false };
            next[event.claimantId] = { ...existing, name: event.claimantName, score: existing.score + event.points };
            return next;
          });
        }
      } else if (event.type === 'wordRejected') {
        // Someone beat us to those balls. Show a quick "stolen" tag.
        setBsToast(t('wordStolen'));
        setTimeout(() => setBsToast(null), 1200);
      }
    });
  }, [isOnline, chatOpen, peerScores]);

  // Auto-scroll chat to bottom on new message
  useEffect(() => {
    if (chatListRef.current) chatListRef.current.scrollTop = chatListRef.current.scrollHeight;
  }, [chatMessages, chatOpen]);

  // Track laser armed state for UI feedback
  useEffect(() => {
    const id = setInterval(() => setLaserArmedUI(isLaserActive()), 100);
    return () => clearInterval(id);
  }, []);

  // Online: rebroadcast our score every 750ms so peers self-heal if a
  // single scoreUpdate message is dropped (manifested as a stale opponent
  // score sticking by ~1 word).
  useEffect(() => {
    if (!isOnline) return;
    const id = setInterval(() => {
      if (!isMultiplayerConnected()) return;
      mpSendScoreUpdate(getCurrentScore(), getCurrentLevel());
    }, 750);
    return () => clearInterval(id);
  }, [isOnline]);

  // Fixed-size scale-to-fit
  useEffect(() => {
    const apply = () => {
      const el = containerRef.current;
      if (!el) return;
      const sx = window.innerWidth / REF_W;
      const sy = window.innerHeight / REF_H;
      const s = Math.min(sx, sy, 1.4);
      el.style.transform = `scale(${s})`;
    };
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, []);

  // Game lifecycle
  useEffect(() => {
    if (started.current) return;
    started.current = true;

    setMultiTouchMode(isDuel);
    setDuelScoreCallback((p1, p2) => setDuel({ p1, p2 }));
    // Miroir: route word submissions through the worker for first-claim-wins
    // arbitration. setMiroirMode must be called BEFORE startGame so the engine
    // takes the claim path on the very first valid word.
    const isMiroir = isOnline && gameMode === 'miroir';
    setMiroirMode(isMiroir);
    if (isMiroir) {
      setOnSendWordClaim((rid, ballIds, word, points) => mpSendWordClaim(rid, ballIds, word, points));
    } else {
      setOnSendWordClaim(() => {});
    }

    initGame({
      onGameOver: (stats) => {
        if (isLoggedIn() && !isDuel) {
          submitScore(stats.score, Math.floor(stats.score / 50) + 1, stats.wordsFound, stats.bestWord);
        }
        // Hidden Elo update for AI matches only
        if (isAI) {
          const botElo = botEloRef.current || getElo();
          updateEloFromMatch(stats.score, getOpponentScore(), botElo);
        }
        // Online: stamp the match outcome. If a peer busted first we already
        // captured that in matchResultRef; otherwise WE busted, so it's a loss.
        let finalStats = stats;
        if (isOnline) {
          if (matchResultRef.current) {
            finalStats = { ...stats, result: matchResultRef.current.result, loserName: matchResultRef.current.loserName };
          } else {
            // We game-overed first — tell peers + mark ourselves the loser.
            mpSendGameOver(stats.score);
            finalStats = { ...stats, result: 'loss' };
          }
        }
        onGameOver(finalStats);
      },
      onScoreUpdate: (score, level) => {
        if (isOnline) mpSendScoreUpdate(score, level);
      },
      onGaugeUpdate: (g, m) => { setGauge(g); setGaugeMax(m); },
    });

    if (isOnline) setMultiplayerCallback((type) => mpSendInterference(type));

    startGame(seedOverride);

    return () => {
      stopGame();
      setMultiTouchMode(false);
      setDuelScoreCallback(() => {});
      started.current = false;
    };
  }, [onGameOver, seedOverride, mode, isOnline, isDuel, isAI, gameMode]);

  const gaugePct = Math.round((gauge / gaugeMax) * 100);
  const canCastWild = gauge >= costs.wildcard;
  const canCastBarrage = gauge >= costs.barrage && isOnline;
  const canCastLaser = gauge >= costs.laser && !laserArmedUI;
  const canCastFreeze = gauge >= costs.freeze && isOnline;
  const canCastBlackout = gauge >= costs.blackout && isOnline;
  const canCastScramble = gauge >= costs.scramble && isOnline;
  const canCastFlood = gauge >= costs.flood && isOnline;

  const handleSendChat = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const text = chatInput.trim();
    if (!text) return;
    if (!isMultiplayerConnected()) return;
    mpSendChat(text);
    setChatMessages(prev => [...prev.slice(-29), { from: 'me', text, ts: Date.now() }]);
    setChatInput('');
  };

  const toggleChat = () => {
    setChatOpen(o => {
      if (!o) setUnreadChat(0);
      return !o;
    });
  };

  // Build a debug snapshot of THIS player's screen state. Used both by the
  // local BS press (sender) and by the auto-responder when a peer asks for it.
  // Reassigned in an effect so the ref always points at a builder that closes
  // over the latest state without needing to re-register the WS handler.
  useEffect(() => {
    buildLocalSnapshotRef.current = () => {
      const snap = getDebugSnapshot();
      const me = getUser();
      return {
        ts: new Date().toISOString(),
        mode,
        aiName,
        botElo: isAI ? botEloRef.current : undefined,
        botVocabSize: isAI ? botRef.current?.getVocabSize?.() : undefined,
        user: me ? { id: me.id, username: me.username } : null,
        peerScores: isOnline ? peerScores : undefined,
        lang: localStorage.getItem('wardogs_lang') || 'fr',
        viewport: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio },
        ua: navigator.userAgent,
        url: window.location.href,
        ...snap,
      };
    };
  }, [mode, aiName, isAI, isOnline, peerScores]);
  const buildLocalSnapshot = () => buildLocalSnapshotRef.current();

  const handleCallBs = () => {
    const ownSnap = buildLocalSnapshot();
    const reqId = `bs_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    const myId = getMultiplayerState().playerId;
    const myName = (getUser()?.username) || 'me';

    // In online mode, fan out a request and collect peer snapshots before
    // shipping the bundle. Otherwise we just ship our own snapshot.
    if (isOnline && isMultiplayerConnected()) {
      bsBuffersRef.current.set(reqId, []);
      mpSendBsRequest(reqId);
      setBsToast(t('bsCaptured'));
      // Wait briefly for peer responses, then send the bundle. 1500ms is long
      // enough for round-trip even on flaky mobile but short enough that the
      // user gets feedback quickly.
      window.setTimeout(() => {
        const peerSnaps = bsBuffersRef.current.get(reqId) || [];
        bsBuffersRef.current.delete(reqId);
        const bundle = {
          requestId: reqId,
          requester: { id: myId, name: myName },
          self: ownSnap,
          peers: peerSnaps,
          peerCount: peerSnaps.length,
        };
        try {
          const key = 'wardogs_bs_reports';
          const existing = JSON.parse(localStorage.getItem(key) || '[]');
          existing.push(bundle);
          while (existing.length > 50) existing.shift();
          localStorage.setItem(key, JSON.stringify(existing));
        } catch (e) {
          console.warn('[bs] failed to persist report', e);
        }
        console.warn('[BS BUNDLE]', bundle);
        submitBsReport(bundle).then(ok => {
          setBsToast(ok ? `${t('bsSent')} (${peerSnaps.length + 1})` : t('bsCaptured'));
          setTimeout(() => setBsToast(null), 1800);
        });
        try {
          navigator.clipboard?.writeText(JSON.stringify(bundle, null, 2));
        } catch { /* clipboard may be unavailable */ }
      }, 1500);
      return;
    }

    // Offline / AI / duel — single-snapshot report (legacy behavior).
    const report = { requestId: reqId, self: ownSnap };
    try {
      const key = 'wardogs_bs_reports';
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      existing.push(report);
      while (existing.length > 50) existing.shift();
      localStorage.setItem(key, JSON.stringify(existing));
    } catch (e) {
      console.warn('[bs] failed to persist report', e);
    }
    console.warn('[BS REPORT]', report);
    submitBsReport(report).then(ok => {
      setBsToast(ok ? t('bsSent') : t('bsCaptured'));
      setTimeout(() => setBsToast(null), 1800);
    });
    try {
      navigator.clipboard?.writeText(JSON.stringify(report, null, 2));
    } catch { /* clipboard may be unavailable */ }
    setBsToast(t('bsCaptured'));
  };

  const handleQuit = () => {
    if (!window.confirm(t('quitConfirm'))) return;
    // forceGameOver triggers onGameOver, which already sends mpSendGameOver
    // for online matches with no recorded result — quitting counts as a loss.
    forceGameOver();
  };

  return (
    <div className="screen active" id="game-screen">
      <div id="game-fit">
        <div id="game-container" ref={containerRef}>
          {isDuel ? (
            <div id="score-bar" className="duel-bar">
              <div className="duel-side p1"><span className="score-label">{t('p1')}</span><span>{duel.p1}</span></div>
              <div className="score-center">
                <span id="current-word-display"></span>
                <span id="combo-display" className="combo-display" style={{ opacity: 0 }}></span>
              </div>
              <div className="duel-side p2"><span className="score-label">{t('p2')}</span><span>{duel.p2}</span></div>
            </div>
          ) : (
            <div id="score-bar">
              <div className="score-left">
                <span className="score-label">{t('score')}</span>
                <span id="current-score">0</span>
              </div>
              <div className="score-center">
                <span id="current-word-display"></span>
                <span id="combo-display" className="combo-display" style={{ opacity: 0 }}></span>
              </div>
              <div className="score-right">
                <span className="score-label">
                  {isOnline ? t('vs') : isAI ? (aiName || t('aiLabel')) : t('level')}
                </span>
                {showOpponent
                  ? <span>{oppScore}</span>
                  : <span id="current-level">1</span>}
              </div>
            </div>
          )}

          {isOnline && Object.keys(peerScores).length > 0 && (
            <div className="mp-peer-scores">
              {Object.entries(peerScores)
                .sort((a, b) => b[1].score - a[1].score)
                .slice(0, 3)
                .map(([id, p]) => (
                  <div key={id} className={`mp-peer-row ${p.finished ? 'finished' : ''}`}>
                    <span className="mp-peer-row-name">{p.name}</span>
                    <span className="mp-peer-row-score">{p.score}</span>
                  </div>
                ))}
            </div>
          )}

          <canvas id="game-canvas"></canvas>

          <button className="quit-btn" onClick={handleQuit} title={t('quit')} aria-label={t('quit')}>
            ✕
          </button>

          <button
            className="bs-btn"
            onClick={handleCallBs}
            title={t('callBs')}
            aria-label={t('callBs')}
          >
            🚩 BS
          </button>

          {bsToast && <div className="bs-toast">{bsToast}</div>}

          <button
            className="rush-btn"
            onPointerDown={(e) => { e.preventDefault(); setRushOverride(true); }}
            onPointerUp={() => setRushOverride(false)}
            onPointerLeave={() => setRushOverride(false)}
            onPointerCancel={() => setRushOverride(false)}
            onContextMenu={(e) => e.preventDefault()}
            aria-label="Rush"
          >
            ⏬ RUSH
          </button>

          {isOnline && (
            <>
              <button
                className={`chat-toggle ${unreadChat > 0 ? 'has-unread' : ''}`}
                onClick={toggleChat}
                aria-label="Chat"
              >
                💬{unreadChat > 0 ? <span className="chat-unread">{unreadChat}</span> : null}
              </button>

              {chatOpen && (
                <div className="chat-panel">
                  <div className="chat-header">
                    <span>{t('vs')}</span>
                    <button className="chat-close" onClick={toggleChat}>×</button>
                  </div>
                  <div className="chat-list" ref={chatListRef}>
                    {chatMessages.length === 0 && <div className="chat-empty">…</div>}
                    {chatMessages.map((m, i) => (
                      <div key={i} className={`chat-msg ${m.from}`}>{m.text}</div>
                    ))}
                  </div>
                  <form className="chat-input-row" onSubmit={handleSendChat}>
                    <input
                      type="text"
                      maxLength={200}
                      value={chatInput}
                      onChange={e => setChatInput(e.target.value)}
                      placeholder="…"
                      autoComplete="off"
                    />
                    <button type="submit" disabled={!chatInput.trim()}>↑</button>
                  </form>
                </div>
              )}
            </>
          )}

          <div id="gauge-bar">
            <div className="gauge-track">
              <div className="gauge-fill" style={{ width: `${gaugePct}%` }} />
              <span className="gauge-label">{gauge}/{gaugeMax}</span>
            </div>
            <div className="power-buttons">
              <button
                className={`power-btn ${canCastWild ? 'ready' : ''}`}
                disabled={!canCastWild}
                onClick={() => castWildcardBall()}
                title={`Wildcard ball (${costs.wildcard})`}
              >
                ★ <span className="power-cost">{costs.wildcard}</span>
              </button>
              <button
                className={`power-btn ${canCastLaser ? 'ready' : ''} ${laserArmedUI ? 'armed' : ''}`}
                disabled={!canCastLaser}
                onClick={() => castLaser()}
                title={`${t('pawSlash')} (${costs.laser})`}
              >
                🐾 <span className="power-cost">{costs.laser}</span>
              </button>
              <button
                className={`power-btn ${canCastBarrage ? 'ready' : ''}`}
                disabled={!canCastBarrage}
                onClick={() => castBarrage()}
                title={`5-ball barrage (${costs.barrage})`}
              >
                ☄ x5 <span className="power-cost">{costs.barrage}</span>
              </button>
            </div>

            {isOnline && (
              <div className="power-buttons sabotage-row">
                <button
                  className={`power-btn sabotage ${canCastFreeze ? 'ready' : ''}`}
                  disabled={!canCastFreeze}
                  onClick={() => castFreeze()}
                  title={t('perkFreeze')}
                  aria-label={t('perkFreeze')}
                >
                  🧊 <span className="power-cost">{costs.freeze}</span>
                </button>
                <button
                  className={`power-btn sabotage ${canCastBlackout ? 'ready' : ''}`}
                  disabled={!canCastBlackout}
                  onClick={() => castBlackout()}
                  title={t('perkBlackout')}
                  aria-label={t('perkBlackout')}
                >
                  🌑 <span className="power-cost">{costs.blackout}</span>
                </button>
                <button
                  className={`power-btn sabotage ${canCastScramble ? 'ready' : ''}`}
                  disabled={!canCastScramble}
                  onClick={() => castScramble()}
                  title={t('perkScramble')}
                  aria-label={t('perkScramble')}
                >
                  🔀 <span className="power-cost">{costs.scramble}</span>
                </button>
                <button
                  className={`power-btn sabotage ${canCastFlood ? 'ready' : ''}`}
                  disabled={!canCastFlood}
                  onClick={() => castFlood()}
                  title={t('perkFlood')}
                  aria-label={t('perkFlood')}
                >
                  💧 <span className="power-cost">{costs.flood}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
